import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  IconButton,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Box
} from '@mui/material';
import { RiMenu2Line } from 'react-icons/ri';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetCurrentUser } from 'apis/user/hook';
import Spinner from 'components/Common/Spinner';
import * as urls from 'routes/config';
import BusinessDropdown from './BusinessDropdown';
import { FiPlus } from 'react-icons/fi';
import { useGetBusinessProfile } from 'apis/business/hook';
import { useBusiness } from 'providers/context/BusinessProvider';
import Modal from 'components/Common/Modal';
import BusinessForm from 'containers/Agency/Dashboard/components/BusinessForm';
import SettingIcon from 'assets/icons/SettingIcon';
import { useGetOrganizations } from 'apis/agency/organizations/hook';

interface HeaderProps {
  onMenuClick?: () => void;
}

export default function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { workspaceId } = useParams<{ businessId: string; workspaceId: string }>();
  const { selectedBusiness } = useBusiness();
  const { data: organizations } = useGetOrganizations();
  const location = useLocation();

  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { data: currentUser, isLoading: isCurrentUserLoading } = useGetCurrentUser();

  const { data: currentBusiness, isLoading: isCurrentBusinessLoading } = useGetBusinessProfile({
    queryKey: ['businessProfile', workspaceId],
    enabled: Boolean(!currentUser?.agency_user && !isCurrentUserLoading)
  });

  if (isCurrentUserLoading || isCurrentBusinessLoading) {
    return <Spinner />;
  }

  return (
    <AppBar
      position='fixed'
      sx={{
        backgroundColor: theme.palette.background.paper,
        boxShadow: 'none',
        borderBottom: '1px solid #E0E0E0',
        top: location.pathname.includes('agency') ? 32 : 0
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', color: '#09090A' }}>
          <Link to='/'>
            <img
              src={organizations?.logo_url}
              alt='Logo'
              style={{
                height: 40,
                paddingRight: 10,
                borderRight: isMediumScreen ? 'none' : '1px solid #cecece'
              }}
            />
          </Link>
          {selectedBusiness && (
            <Link to={urls.business.base}>
              <IconButton
                color='primary'
                sx={{
                  background: theme.palette.primary.light,
                  color: theme.palette.primary.main,
                  borderRadius: 0.5,
                  marginX: 2,
                  display: isMediumScreen ? 'none' : 'inherit'
                }}
              >
                <Box display='flex'>
                  <SettingIcon />
                </Box>
              </IconButton>
            </Link>
          )}
          {Boolean(currentUser?.agency_user) ? <BusinessDropdown /> : currentBusiness?.name}
          {Boolean(currentUser?.agency_user) && (
            <Modal
              button={(openModal: any) => (
                <IconButton
                  color='primary'
                  sx={{
                    background: theme.palette.primary.light,
                    borderRadius: 0.5,
                    marginX: 1,
                    display: isMediumScreen ? 'none' : 'inherit'
                  }}
                  onClick={openModal}
                >
                  <FiPlus style={{ fontWeight: 200, display: 'flex' }} />
                </IconButton>
              )}
            >
              {(handleCloseModal: any) => <BusinessForm handleClose={handleCloseModal} />}
            </Modal>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isMediumScreen && (
            <IconButton color='primary' edge='start' onClick={onMenuClick}>
              <RiMenu2Line />
            </IconButton>
          )}

          <Typography
            variant='body1'
            sx={{
              color: '#09090A',
              marginRight: 1,
              display: isMediumScreen ? 'none' : 'inherit'
            }}
          >
            {currentUser?.name}
          </Typography>
          <IconButton onClick={handleClick} sx={{ display: isMediumScreen ? 'none' : 'inherit' }}>
            <Avatar
              src={currentUser?.avatar_url}
              alt={currentUser?.name || 'Dummy'}
              sx={{
                width: 34,
                height: 34
              }}
            />
          </IconButton>
          {!isMediumScreen && (
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
              slotProps={{ paper: { sx: { width: 205 } } }}
            >
              <MenuItem onClick={() => navigate(`${urls.base(workspaceId)}/${urls.home.profile}`)}>Profile</MenuItem>
              <MenuItem onClick={() => navigate(`${urls.auth.base}/${urls.auth.logout}`)}>Logout</MenuItem>
            </Menu>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}
